import React, { useCallback, useState } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import "mapbox-gl/dist/mapbox-gl.css";
import Map, {
  AttributionControl,
  Layer,
  MapEvent,
  MapStyleDataEvent,
  Source,
} from "react-map-gl";

import { getAreaBaseLayer } from "./layers";
import { useMapHeight, useMapStyle } from "./config";
import { PenPortraitMapOverlay } from "./PenPortraitMapOverlay";
import { LocationResult } from "@/types";
import { extractBreaks, getFillColor } from "@/utils";

type PenPortraitMapProps = {
  scores: LocationResult;
  extra: boolean;
};

const PenPortraitMap: React.FC<PenPortraitMapProps> = ({ scores, extra }) => {
  const theme = useTheme();
  const mapStyle = useMapStyle(theme.palette.mode);
  const mapHeight = useMapHeight(extra);

  const [firstTime, setFirstTime] = useState<boolean>(true);

  const onLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      map &&
        scores.area.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "area-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level }
          );
        });
      map &&
        map.setPaintProperty(
          "area-base",
          "fill-color",
          getFillColor(extractBreaks(scores.area), theme.palette.gradient),
        );
    },
    [scores]
  );

  const onStyleData = useCallback((event: MapStyleDataEvent) => {
    setFirstTime(false);
  }, []);

  return (
    <Box
      height={mapHeight}
      minHeight={mapHeight}
      // minWidth='275px'
      position="relative"
      sx={{
        overflow: "hidden",
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        ".mapbox-improve-map": {
          display: "none",
        },
        ".mapboxgl-ctrl.mapboxgl-ctrl-attrib": {
          backgroundColor: "transparent !important",
        },
        ".mapboxgl-ctrl-attrib-inner": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-ctrl-attrib a": {
          color: theme.palette.text.secondary,
        },
      }}
    >
      <Map
        initialViewState={{
          longitude: -4.3,
          latitude: 54.8,
          zoom: 1,
        }}
        projection={{ name: "mercator" }}
        minZoom={4}
        maxBounds={[
          [-30, 36],
          [30, 66],
        ]}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_MAP_TOKEN}
        onLoad={onLoad}
        onStyleData={onStyleData}
        attributionControl={false}
        touchPitch={false}
        dragPan={false}
        dragRotate={false}
        interactive={false}
      >
        <AttributionControl customAttribution=" " />
        <Source
          id="area-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_area_test"
        >
          <Layer beforeId="waterway-label" {...getAreaBaseLayer(theme)} />
        </Source>
        <PenPortraitMapOverlay scores={scores} firstTime={firstTime} />
      </Map>
    </Box>
  );
};

export default PenPortraitMap;
